import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FiPlus, FiX } from 'react-icons/fi';
import { getAllTags } from 'api/tags';
import Select from 'react-select';

import { WidgetType, type WidgetConfig, type DxfWidget } from '../hooks/useDxfWidgets';

/**
 * Dynamic widget configuration modal with multiple tag inputs
 */
const WIDGET_LABELS: Record<WidgetType, string> = {
  large: '3 Numbers & Graph',
  gaugeBar: '4 Gauge / Graph & Bar',
  gauge: '1 Gauge / Graph',
  area: '1 Graph & Bar',
  gaugePage: '4 Gauges',
  column: '4 Column Charts',
  kpi: '4 Numbers & Unit',
};

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSave: (w: { type: WidgetType; config: WidgetConfig }) => void;
  editingWidget?: DxfWidget | null;
}

const TAG_LIMIT: Record<WidgetType, number> = {
  large: 3,
  gaugeBar: 4,
  gauge: 1,
  area: 1,
  gaugePage: 4,
  column: 4,
  kpi: 4,
};

interface TagMeta {
  id: number;
  name: string;
  tagNameIdentifier?: string;
}

interface TagInput {
  name: string;
  tagId: number | null;
}

const AddWidgetModal: React.FC<Props> = ({ open, onOpenChange, onSave, editingWidget }) => {
  const [selectedType, setSelectedType] = useState<WidgetType>('large');
  const [widgetName, setWidgetName] = useState('New Widget');
  const [tagInputs, setTagInputs] = useState<TagInput[]>([]);
  const [allTags, setAllTags] = useState<TagMeta[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  useEffect(() => {
    setIsLoadingTags(true);
    getAllTags().then((res) => {
      setAllTags(res.data || []);
    }).catch((err) => {
      // Handle error silently
    }).finally(() => {
      setIsLoadingTags(false);
    });
  }, []);

  // Update tag inputs when widget type changes
  useEffect(() => {
    // Only update tag inputs if we're not editing a widget
    if (!editingWidget) {
      const limit = TAG_LIMIT[selectedType];
      setTagInputs(Array(limit).fill(null).map(() => ({ name: '', tagId: null })));
    }
  }, [selectedType, editingWidget]);

  // Pre-fill form when editing a widget
  useEffect(() => {
    if (editingWidget && open && allTags.length > 0) {

      setSelectedType(editingWidget.type);
      setWidgetName(editingWidget.config.name);

      // Pre-fill tag inputs with existing data
      const existingTagIds = editingWidget.config.tagIds || [];
      const existingTagNames = editingWidget.config.tagNames || [];

      const limit = TAG_LIMIT[editingWidget.type];
      const preFilledInputs = Array(limit).fill(null).map((_, index) => {
        if (index < existingTagIds.length) {
          // Find the tag in allTags to get the proper name if tagNames is empty
          const tag = allTags.find(t => t.id === existingTagIds[index]);
          const tagName = existingTagNames[index] || tag?.name || `Tag ${existingTagIds[index]}`;


          return {
            name: tagName,
            tagId: existingTagIds[index]
          };
        }
        return { name: '', tagId: null };
      });

      setTagInputs(preFilledInputs);
    } else if (!editingWidget && open) {
      // Reset form for new widget
      setSelectedType('large');
      setWidgetName('New Widget');
      const limit = TAG_LIMIT['large'];
      setTagInputs(Array(limit).fill(null).map(() => ({ name: '', tagId: null })));
    }
  }, [editingWidget, open, allTags]);

  const handleSave = () => {
    if (isLoadingTags) {
      alert('Please wait for tags to load before saving');
      return;
    }

    const validInputs = tagInputs.filter(input => input.tagId !== null);

    if (validInputs.length === 0) {
      alert('Please select at least one tag');
      return;
    }

    // Create tag configuration with names and IDs
    const tagConfigs = validInputs.map(input => {
      const tag = allTags.find(t => t.id === input.tagId);
      return {
        id: input.tagId!,
        name: input.name || tag?.name || `Tag ${input.tagId}`,
        originalName: tag?.name || `Tag ${input.tagId}`
      };
    });

    onSave({
      type: selectedType,
      config: {
        name: widgetName,
        tagIds: tagConfigs.map(t => t.id),
        tagNames: tagConfigs.map(t => t.name)
      },
    });

    // Close the modal after saving
    onOpenChange(false);

    // Reset form
    setSelectedType('large');
    setWidgetName('New Widget');
    setTagInputs([]);
  };

  const updateTagInput = (index: number, field: 'name' | 'tagId', value: string | number | null) => {
    const newInputs = [...tagInputs];
    newInputs[index] = { ...newInputs[index], [field]: value };
    setTagInputs(newInputs);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-white/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 dark:bg-slate-950/80 z-50" />
        <Dialog.Content 
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black w-[700px] z-50 text-white max-h-[90vh] overflow-visible rounded-lg" 
          style={{ pointerEvents: 'auto' }}
          onInteractOutside={(e) => {
            e.preventDefault(); // Prevent closing on outside click
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <div>
              <Dialog.Title className="text-lg text-white">{editingWidget ? 'Edit Widget' : 'Add Widget'}</Dialog.Title>
              <Dialog.Description className="text-[#a1a1aa] text-sm">
                {editingWidget ? 'Update your widget settings' : 'Configure your new widget settings'}
              </Dialog.Description>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="p-1 hover:bg-gray-800 rounded transition-colors"
            >
              <FiX size={20} />
            </button>
          </div>

          {/* Scrollable content area */}
          <div className="px-6 pb-6 max-h-[calc(90vh-120px)] overflow-y-auto space-y-4">

            {/* Widget type picker */}
            <div className="grid gap-2">
              <label className="text-white text-sm">Widget Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as WidgetType)}
                className="input"
              >
                {(Object.keys(WIDGET_LABELS) as WidgetType[]).map((k) => {
                  if (k === 'gaugePage') return null;
                  return (
                    <option key={k} value={k}>
                      {WIDGET_LABELS[k]}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Widget Name */}
            <div className="grid gap-2">
              <label className="text-white text-sm">Widget Name</label>
              <input
                value={widgetName}
                onChange={(e) => setWidgetName(e.target.value)}
                className="input"
                placeholder="Enter widget name..."
              />
            </div>

            {/* Dynamic Tag Inputs */}
            <div className="grid gap-2">
              <label className="text-white text-sm">
                Configure Tags ({TAG_LIMIT[selectedType]} required)
              </label>
              {isLoadingTags && (
                <div className="text-yellow-400 text-sm">
                  Loading available tags... Please wait.
                </div>
              )}
              <div className="space-y-3">
                {tagInputs.map((input, index) => (
                  <div key={index} className="flex gap-3">
                    {/* Name Input */}
                    <div className="flex-1">
                      <input
                        type="text"
                        value={input.name}
                        onChange={(e) => updateTagInput(index, 'name', e.target.value)}
                        className="input"
                        placeholder="Name (optional)"
                      />
                    </div>

                    {/* Tag Selection */}
                    <div className="flex-1">
                      <Select
                        value={allTags.find(tag => tag.id === input.tagId) ? {
                          value: input.tagId,
                          label: `${allTags.find(tag => tag.id === input.tagId)?.tagNameIdentifier || allTags.find(tag => tag.id === input.tagId)?.name} (ID: ${input.tagId})`
                        } : null}
                        onChange={(option) => updateTagInput(index, 'tagId', option ? option.value : null)}
                        options={allTags.map(tag => ({
                          value: tag.id,
                          label: `${tag.tagNameIdentifier || tag.name} (ID: ${tag.id})`
                        }))}
                        placeholder={isLoadingTags ? 'Loading tags...' : 'Select Value'}
                        isSearchable
                        isClearable
                        isDisabled={isLoadingTags}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        menuPlacement="auto"
                        onMenuOpen={() => {
                          // Add wheel event listener to the dropdown menu for proper scrolling
                          setTimeout(() => {
                            const menuElement = document.querySelector('.react-select__menu');
                            if (menuElement) {
                              const handleWheel = (e: Event) => {
                                const wheelEvent = e as WheelEvent;
                                wheelEvent.stopPropagation();
                                const target = wheelEvent.currentTarget as HTMLElement;
                                const { scrollTop, scrollHeight, clientHeight } = target;
                                const wheelDelta = wheelEvent.deltaY;

                                // Allow scrolling within the menu
                                if (scrollTop > 0 || scrollTop + clientHeight < scrollHeight) {
                                  wheelEvent.preventDefault();
                                  target.scrollTop += wheelDelta;
                                }
                              };

                              menuElement.addEventListener('wheel', handleWheel, { passive: false });

                              // Store the handler for cleanup
                              (menuElement as any)._wheelHandler = handleWheel;
                            }
                          }, 0);
                        }}
                        onMenuClose={() => {
                          // Clean up wheel event listener
                          const menuElement = document.querySelector('.react-select__menu');
                          if (menuElement && (menuElement as any)._wheelHandler) {
                            menuElement.removeEventListener('wheel', (menuElement as any)._wheelHandler);
                            delete (menuElement as any)._wheelHandler;
                          }
                        }}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            backgroundColor: '#0c3756',
                            borderColor: state.isFocused ? '#3b82f6' : '#4d4d4d',
                            borderWidth: '1px',
                            borderRadius: '6px',
                            minHeight: '36px',
                            boxShadow: 'none',
                            '&:hover': {
                              borderColor: '#6b7280'
                            }
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: '2px 8px',
                            color: 'white'
                          }),
                          input: (provided) => ({
                            ...provided,
                            color: 'white'
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#9ca3af'
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: 'white',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }),
                          menu: (provided) => ({
                            ...provided,
                            backgroundColor: '#0c3756',
                            border: '1px solid #4d4d4d',
                            borderRadius: '6px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            minWidth: '100%',
                            width: 'max-content',
                            zIndex: 999999
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            padding: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#4d4d4d #0c3756'
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? '#3b82f6'
                              : state.isFocused
                                ? '#1a1a1a'
                                : 'transparent',
                            color: state.isSelected ? 'white' : '#d1d5db',
                            borderRadius: '4px',
                            margin: '2px 0',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            pointerEvents: 'auto',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            '&:hover': {
                              backgroundColor: state.isSelected ? '#3b82f6' : '#1a1a1a',
                              color: 'white'
                            }
                          }),
                          clearIndicator: (provided) => ({
                            ...provided,
                            color: '#9ca3af',
                            '&:hover': {
                              color: 'white'
                            }
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#9ca3af',
                            '&:hover': {
                              color: 'white'
                            }
                          }),
                          menuPortal: (provided) => ({
                            ...provided,
                            zIndex: 999999
                          })
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 px-6 pb-6">
            <button
              className="btn-sec"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </button>
            <button
              className={`btn flex items-center gap-2 ${isLoadingTags ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handleSave}
              disabled={isLoadingTags}
            >
              <FiPlus size={16} /> {editingWidget ? 'Update Widget' : 'Add Widget'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AddWidgetModal;
